import { CommonModule, NgComponentOutlet } from '@angular/common';
import {
  AfterContentInit,
  AfterViewChecked,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  Type,
  untracked,
  viewChild,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AI_SUMMARY_COMPONENT,
  AiSummaryComponentLike,
  CrmDependencies,
  CrmInjectionToken,
  ObjectType,
  PrimaryAccountGroupIDForCrmObject,
} from '../../tokens-and-interfaces';
import { tableFiltersInjectionTokenGenerator } from '../../injection-token-factories';
import { SEARCH_DEBOUNCE_MS } from '../../constants';
import { TranslationModule } from '../../i18n';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  map,
  of,
  ReplaySubject,
  shareReplay,
  skipWhile,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { CrmActivityLoggerComponent } from './activities';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CrmTimelineComponent } from './timeline';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyFilterChipsComponent } from '@vendasta/galaxy/filter/chips/src/galaxy-filter-chips.component';
import {
  GalaxyFilterChipDependencies,
  GalaxyFilterChipInjectionToken,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatIconModule } from '@angular/material/icon';
import { InfiniteScrollTriggerComponent } from '@vendasta/galaxy/infinite-scroll-trigger';
import { ActivityInterface,ListViewConfigurationsResponseInterface, View, ViewConfigurationsApiService } from '@vendasta/crm';
import { RxState } from '@rx-angular/state';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { decodeFilters, encodeFilters, validateFilters } from '@vendasta/galaxy/filter/chips/src/utils';
import { ActivitiesTimelineService } from './timeline';
import { SavedFilter, SavedFilters } from '../filter-view';
import { CrmFieldService, PlatformExtensionFieldIds, StandardIds } from '@galaxy/crm/services/field-service';
import { CrmObjectService } from '../../shared-services';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { TabGroup, ViewTabsComponent } from '@galaxy/crm/components/view-tabs';
import { catchError, startWith } from 'rxjs/operators';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatSlideToggle } from '@angular/material/slide-toggle';

@Component({
  selector: 'crm-activities-timeline',
  templateUrl: './activities-timeline.component.html',
  styleUrls: ['./activities-timeline.component.scss'],
  providers: [
    RxState,
    ActivitiesTimelineService,
    {
      provide: GalaxyFilterChipInjectionToken,
      useFactory: tableFiltersInjectionTokenGenerator('Activity'),
    },
    {
      provide: PrimaryAccountGroupIDForCrmObject,
      useFactory: (
        comp: CrmActivitiesTimelineComponent,
        objectService: CrmObjectService,
        fieldService: CrmFieldService,
      ) => {
        const crmObjectId$ = toObservable(comp.crmObjectId);
        const crmObjectType$ = toObservable(comp.activityObjectType).pipe(filter(Boolean));
        return combineLatest([crmObjectId$, crmObjectType$]).pipe(
          switchMap((v: [string, ObjectType]) => {
            const crmObjectId = v[0];
            const crmObjectType = v[1];
            return objectService.getMultiObject(crmObjectType, [crmObjectId]);
          }),
          map((response) => response?.crmObjects?.[0]),
          map((crmObject) => {
            return (
              fieldService.getFieldValueFromCrmObject(crmObject!, PlatformExtensionFieldIds.AccountGroupID)
                ?.stringValue ?? null
            );
          }),
          filter(Boolean),
          shareReplay(1),
        );
      },
      deps: [CrmActivitiesTimelineComponent, CrmObjectService, CrmFieldService],
    },
  ],
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          height: '*',
          opacity: 1,
        }),
      ),
      state(
        'closed',
        style({
          height: '0px',
          opacity: 0,
        }),
      ),
      transition('open <=> closed', [animate('0.2s')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    NgComponentOutlet,
    TranslateModule,
    TranslationModule,
    CrmActivityLoggerComponent,
    CrmTimelineComponent,
    GalaxyFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    GalaxyFilterChipsComponent,
    MatIconModule,
    InfiniteScrollTriggerComponent,
    GalaxyAlertModule,
    ViewTabsComponent,
    MatSlideToggle,
    FormsModule,
  ],
})
export class CrmActivitiesTimelineComponent implements OnDestroy, OnInit, AfterContentInit, AfterViewChecked {
  timeline = viewChild(CrmTimelineComponent);
  @ViewChild('scrollableContainerRef', { static: true }) scrollableContainer!: ElementRef;
  @ViewChild('filterChipRef') filterChipRef?: GalaxyFilterChipsComponent;

  crmObjectId = input<string>('');
  showFiltersOpen = false;

  activityObjectType = input<ObjectType | null>(null);
  objectSubtype = input<string>('');
  enableAISummary = input<boolean>(false);
  aiSummaryComponent = input<Type<AiSummaryComponentLike> | null>(null);

  private readonly excludeCampaignFilters: GalaxyFilterInterface[] = [
    {
      fieldId: StandardIds.ActivityCampaignID,
      filterId: 'exclude-campaign-preset-campaign-id',
      operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY,
    },
    {
      fieldId: StandardIds.ActivityCommunicationType,
      filterId: 'exclude-campaign-preset-comm-type',
      operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT,
      values: [{ string: 'Campaign SMS' }],
    },
  ];

  enableNewActivityFeed = toSignal(this.config?.enableNewActivityFeed$ ?? of(false));

  objectType: ObjectType = 'Activity';

  private readonly translateService = inject(TranslateService);

  private readonly presetViewNameKeyMap: Record<string, string> = {
    All: 'VIEWS.PRESET_FILTERS.ALL',
    Calls: 'VIEWS.PRESET_FILTERS.CALLS',
    Communications: 'VIEWS.PRESET_FILTERS.COMMUNICATIONS',
    Emails: 'VIEWS.PRESET_FILTERS.EMAILS',
    Meetings: 'VIEWS.PRESET_FILTERS.MEETINGS',
    Notes: 'VIEWS.PRESET_FILTERS.NOTES',
    'Recorded Meetings': 'VIEWS.PRESET_FILTERS.RECORDED_MEETINGS',
    Tasks: 'VIEWS.PRESET_FILTERS.TASKS',
  };
  private readonly destroyRef = inject(DestroyRef);
  private readonly injectedAiSummaryComponent = inject(AI_SUMMARY_COMPONENT, { optional: true });

  protected readonly filters = signal<GalaxyFilterInterface[]>([]);
  protected readonly filtersApplied = computed(() => this.filters().length > 0);

  /**
   * Resolves the AI Summary component to use, preferring the input over the injected token.
   * Returns null if neither is provided, which will result in no AI Summary being rendered.
   */
  protected readonly resolvedAiSummaryComponent = computed(() => {
    return this.aiSummaryComponent() ?? this.injectedAiSummaryComponent ?? null;
  });

  searchControl = new FormControl<string>('');
  subscriptions: Subscription[] = [];
  showLoadMore = false;
  private scrollTop = 0;

  private initialSearchTerm = '';
  private initialSearchTermSet = false;
  private initialFilters: GalaxyFilterInterface[] = [];
  keys: string[];

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly activitiesTimelineService: ActivitiesTimelineService,
    @Inject(GalaxyFilterChipInjectionToken)
    private readonly activitiesTimelineFilterService: GalaxyFilterChipDependencies,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly snackService: SnackbarService,
    private readonly viewConfigService: ViewConfigurationsApiService,
  ) {
    effect(() => {
      //TODO: Remove Monday Nov 24th
      const defaultsKey = this.hideDefaultsViewKey();
      this.hiddenDefaultTabs$$.next(this.loadSavedStringList(defaultsKey));
    });
    effect(() => {
      const viewOrderKey = this.viewTabOrderKey();
      this.tabOrder$$.next(this.loadSavedStringList(viewOrderKey));
    });
    effect(() => {
      this.excludeCampaignsOverride(); //needed to trigger effect
      const timeline = untracked(this.timeline);
      if (!timeline) {
        return;
      }
      const filters = untracked(this.filters);
      this.changeViewFilters(filters);
    });

    const filterParam = this.activatedRoute.snapshot.queryParamMap.get('filter');
    let filters: GalaxyFilterInterface[] = [];
    if (filterParam) {
      const f = decodeFilters(filterParam);
      if (validateFilters(f) && f.length > 0) {
        filters = f;
        const campaignOverride = f.find(
          (filter) =>
            filter.filterId === 'exclude-campaign-preset-campaign-id' ||
            filter.filterId === 'exclude-campaign-preset-comm-type',
        );
        if (campaignOverride) {
          this.excludeCampaignsOverride.set(true);
        }
      }
    } else {
      filters = this.excludeCampaignFilters;
      this.excludeCampaignsOverride.set(true);
    }
    this.initialFilters = filters;
    this.activitiesTimelineFilterService.setInitialAppliedFilters?.(filters);

    const keys: string[] = [];
    if (this.config?.appID) {
      keys.push('crm:' + this.config.appID + ':activities-timeline:views');
    }
    keys.push('crm:activities-timeline:views');
    this.keys = keys;
  }

  ngOnInit() {
    const searchParam = this.activatedRoute.snapshot.queryParamMap.get('search');
    if (searchParam) {
      this.initialSearchTerm = decodeURI(searchParam);
    }
    this.activitiesTimelineService.initializeState(this.initialSearchTerm, this.initialFilters);

    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          skipWhile((term) => term !== this.initialSearchTerm),
          debounceTime(SEARCH_DEBOUNCE_MS),
          map((value) => value?.trim() ?? ''),
          tap((searchTerm) => {
            let searchParam: string | null = null;
            if (searchTerm) {
              searchParam = encodeURI(searchTerm);
            }
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { search: searchParam },
              queryParamsHandling: 'merge',
            });
            const timeline = this.timeline();
            if (timeline) {
              timeline.setSearchTerm(searchTerm);
            }
          }),
        )
        .subscribe(),
    );
  }

  ngAfterViewChecked(): void {
    if (!this.initialSearchTermSet) {
      this.searchControl.setValue(this.initialSearchTerm);
      this.searchControl.updateValueAndValidity();
      this.initialSearchTermSet = true;
    }
  }

  ngAfterContentInit(): void {
    const timeline = this.timeline();
    if (timeline) {
      timeline.filters$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filters) => this.filters.set(filters));
    }
  }

  addActivity(activity: ActivityInterface): void {
    const timeline = this.timeline();
    if (timeline) {
      timeline.addActivity(activity);
    }
  }

  handleActivitiesChanged(activities: ActivityInterface[]): void {
    this.scrollableContainer.nativeElement.scrollTop = this.scrollTop;
    this.showLoadMore = activities?.length > 0;
  }

  loadMoreActivities(): void {
    this.scrollTop = this.scrollableContainer?.nativeElement?.scrollTop ?? 0;
    const timeline = this.timeline();
    if (timeline) {
      timeline.loadMoreActivities();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  toggleFilterBar(): void {
    this.showFiltersOpen = !this.showFiltersOpen;
  }

  updateFilters($event: GalaxyFilterInterface[], updateChipList?: boolean): void {
    const timeline = this.timeline();
    if (!timeline) {
      return;
    }
    let filterParam: string | null = null;
    if ($event.length > 0) {
      const readyFilters = $event.filter(
        (f) => !(f.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS && f.values?.length === 0),
      );
      if (readyFilters.length > 0) {
        filterParam = encodeFilters(readyFilters);
      }
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { filter: filterParam },
      queryParamsHandling: 'merge',
    });
    timeline.setFilters($event);
    if (updateChipList) {
      this.filterChipRef?.filters.set($event);
    }
    this.filters.set($event);
  }

  protected readonly viewFilterKey = computed(() => {
    const subType = this.objectSubtype();
    const objectType = this.activityObjectType() ?? '';
    const objectKey = subType ? subType : objectType;
    return this.config?.appID
      ? 'crm:' + this.config.appID + ':activities-timeline:views:' + objectKey
      : 'crm:' + ':activities-timeline:views:' + objectKey;
  });

  // preset views are not saved locally so we need to handle hiding them differently
  private readonly hideDefaultsViewKey = computed(() => {
    //TODO: Remove Monday Nov 24th
    return this.viewFilterKey() + ':hide-defaults';
  });

  private readonly viewTabOrderKey = computed(() => {
    return this.viewFilterKey() + ':order';
  });

  protected readonly newViewTemplate = computed(() => {
    return {
      name: '',
      filters: this.filters(), //based on current filters
      preset: false,
    } as SavedFilter;
  });

  protected excludeCampaignsOverride = signal(false);
  private readonly hiddenDefaultTabs$$ = new ReplaySubject<string[]>(1);
  protected readonly tabOrder$$ = new ReplaySubject<string[]>(1);

  //reload
  private readonly refreshCloudSavedFilters$$ = new BehaviorSubject(false);
  // cloud saved filters
  private readonly cloudSavedViewFilterConfigs$ = combineLatest([
    this.config.namespace$,
    this.config.currentUserId$,
    toObservable(this.viewFilterKey),
    this.refreshCloudSavedFilters$$,
  ]).pipe(
    switchMap(([namespace, userId, componentKey, _]) => {
      if (!namespace) {
        return of({
          viewConfigurations: [],
        } as ListViewConfigurationsResponseInterface);
      }
      return this.viewConfigService.listViewConfiguration({
        listOptions: {
          namespace: namespace,
          identifier: userId,
          componentIds: [componentKey],
        },
      });
    }),
    map((resp) => resp?.viewConfigurations || []),
    shareReplay(1),
  );

  private readonly cloudSavedViewFilters$ = combineLatest([
    this.cloudSavedViewFilterConfigs$,
    this.config.namespace$,
  ]).pipe(
    switchMap(([viewConfigs, namespace]) => {
      if (viewConfigs.length === 0) {
        return of(null);
      }
      return this.viewConfigService.getMultiView({
        namespace: namespace,
        viewIds: viewConfigs?.map((viewConfig) => viewConfig.viewId).filter((viewId): viewId is string => !!viewId),
      });
    }),
    map((resp) => {
      const mappedResults = new Map<string, View>();
      if (resp?.views) {
        resp.views.forEach((view) => {
          mappedResults.set(view.viewId, view);
        });
      }
      return mappedResults;
    }),
    shareReplay(1),
  );

  private readonly cloudSavedViewFiltersWithConfig$ = combineLatest([
    this.cloudSavedViewFilterConfigs$,
    this.cloudSavedViewFilters$,
  ]).pipe(
    map(([viewConfigs, views]) => {
      const savedFilters: SavedFilter[] = [];
      viewConfigs.forEach((viewConfig) => {
        const view = viewConfig.viewId ? views.get(viewConfig.viewId) : undefined;
        if (view) {
          try {
            const filters = JSON.parse(view.filters);
            if (filters) {
              savedFilters.push({
                name: this.resolvePresetViewName(viewConfig.name ?? '', viewConfig.namespace ?? ''),
                filters: filters,
                hidden: !viewConfig.enabled,
                preset: viewConfig.namespace === 'standard',
                isOverride: viewConfig.isOverride,
                id: viewConfig.viewConfigurationId,
                namespace: viewConfig.namespace,
                identifier: viewConfig.identifier,
              });
            }
          } catch (err) {
            console.error(err);
          }
        }
      });
      return savedFilters;
    }),
    map((savedFilters) => this.sortFilters(savedFilters)),
  );

  protected readonly viewTabsLoading = signal<boolean>(false);

  protected readonly viewTabs$ = combineLatest([this.cloudSavedViewFiltersWithConfig$, this.hiddenDefaultTabs$$]).pipe(
    map(([viewConfigs, hiddenDefaults]) => {
      const standardViews: SavedFilter[] = [];
      const customViews: SavedFilter[] = [];

      viewConfigs.forEach((config) => {
        if (config.preset || config.isOverride) {
          if (config.preset) {
            //TODO: Remove Monday Nov 24th
            config.hidden = hiddenDefaults?.includes(config.id || '') || false;
          }
          standardViews.push(config);
        } else {
          customViews.push(config);
        }
      });

      const tabGroups: TabGroup<SavedFilter>[] = [];

      if (standardViews.length > 0) {
        tabGroups.push({
          name: this.translateService.instant('VIEW_TABS.DEFAULT_VIEWS_TAB_GROUP'),
          id: 'default',
          tabs: standardViews,
          canAddMore: false,
        } as TabGroup<SavedFilter>);
      }

      tabGroups.push({
        name: this.translateService.instant('VIEW_TABS.CUSTOM_VIEWS_TAB_GROUP'),
        id: 'custom',
        tabs: customViews,
        canModify: true,
      } as TabGroup<SavedFilter>);

      return tabGroups;
    }),
    startWith([] as TabGroup<SavedFilter>[]),
  );

  addExcludeCampaignOverride(currentFilters: GalaxyFilterInterface[]): GalaxyFilterInterface[] {
    this.excludeCampaignFilters.forEach((filter) => {
      const alreadyHas = currentFilters.findIndex((currentFilter) => currentFilter.filterId === filter.filterId);
      if (alreadyHas === -1) {
        currentFilters.push(filter);
      }
    });
    return currentFilters;
  }

  private resolvePresetViewName(name: string, namespace: string): string {
    if (namespace !== 'standard') return name;
    const key = this.presetViewNameKeyMap[name];
    return key ? this.translateService.instant(key) : name;
  }

  removeExcludeCampaignOverride(currentFilters: GalaxyFilterInterface[]): GalaxyFilterInterface[] {
    return currentFilters.filter((filter) => {
      return !(
        filter.filterId === 'exclude-campaign-preset-campaign-id' ||
        filter.filterId === 'exclude-campaign-preset-comm-type'
      );
    });
  }

  protected async changeViewFilters(newFilters: GalaxyFilterInterface[]) {
    let filterCopy: GalaxyFilterInterface[] = JSON.parse(JSON.stringify(newFilters));
    const campaignsOverride = this.excludeCampaignsOverride();
    if (campaignsOverride) {
      filterCopy = this.addExcludeCampaignOverride(filterCopy);
    } else {
      filterCopy = this.removeExcludeCampaignOverride(filterCopy);
    }
    this.updateFilters(filterCopy, true);
  }

  private sortFilters(filters: SavedFilters): SavedFilters {
    return filters.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
  }

  private loadSavedStringList(key: string): string[] {
    const hiddenDefaults = localStorage.getItem(key);
    if (hiddenDefaults) {
      try {
        return JSON.parse(hiddenDefaults);
      } catch {
        localStorage.removeItem(key);
      }
    }
    return [];
  }

  protected async deleteViewFilters($event: string[]) {
    if ($event.length == 0) {
      return;
    }
    const namespace = await firstValueFrom(this.config.namespace$);
    const prevViewFilters = await firstValueFrom(this.cloudSavedViewFiltersWithConfig$);
    const pendingDeletes: Promise<boolean>[] = [];
    prevViewFilters.forEach((view) => {
      if (view.id && $event.includes(view.id)) {
        const pendingDelete = this.viewConfigService
          .deleteViewConfiguration({
            namespace: namespace,
            viewConfigurationId: view.id,
          })
          .pipe(
            catchError(() => of(null)),
            map((resp) => {
              return resp?.ok || false;
            }),
          );
        pendingDeletes.push(firstValueFrom(pendingDelete));
      }
    });
    const results = await Promise.all(pendingDeletes);
    const failures = results.filter((result) => !result);
    if (failures.length == results.length) {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.FAILED_TO_DELETE_CUSTOM_VIEWS');
    } else if (failures.length > 0) {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.PARTIAL_FAIL_TO_DELETE_CUSTOM_VIEWS');
    } else {
      this.snackService.openSuccessSnack('VIEW_TABS.SUCCESS.SAVED_CUSTOM_VIEWS');
    }

    // if we refresh right away we won't get the updates
    this.viewTabsLoading.set(true);
    setTimeout(() => {
      this.refreshCloudSavedFilters$$.next(true);
      this.viewTabsLoading.set(false);
    }, 4000);
  }

  protected async saveViewFilterTabChanges($event: TabGroup<SavedFilter>[]) {
    if ($event.length == 0) {
      return;
    }
    const namespace = await firstValueFrom(this.config.namespace$);
    const userId = await firstValueFrom(this.config.currentUserId$);
    const pendingChanges: Promise<boolean>[] = [];
    $event.forEach((group) => {
      if (group.id === 'default') {
        const updates = this.saveDefaultViewFilters(group, namespace, userId);
        pendingChanges.push(...updates);
      }
      if (group.id === 'custom') {
        const updates = this.saveCustomViewFilters(group, namespace, userId);
        pendingChanges.push(...updates);
      }
    });

    if (pendingChanges.length == 0) {
      return;
    }
    const results = await Promise.all(pendingChanges);
    const failures = results.filter((result) => !result);
    if (failures.length == results.length) {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.FAILED_TO_SAVE_CUSTOM_VIEWS');
    } else if (failures.length > 0) {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.PARTIAL_FAIL_TO_SAVE_CUSTOM_VIEWS');
    } else {
      this.snackService.openSuccessSnack('VIEW_TABS.SUCCESS.SAVED_CUSTOM_VIEWS');
    }

    // if we refresh right away we won't get the updates
    this.viewTabsLoading.set(true);
    setTimeout(() => {
      this.refreshCloudSavedFilters$$.next(true);
      this.viewTabsLoading.set(false);
    }, 4000);
  }

  private saveDefaultViewFilters(group: TabGroup<SavedFilter>, namespace: string, userId: string): Promise<boolean>[] {
    return group.tabs.map((tab) => {
      if (tab.isOverride) {
        return firstValueFrom(
          this.viewConfigService
            .updateViewConfigurations({
              viewConfigurationId: tab.id,
              namespace: namespace,
              name: tab.name,
              enabled: !tab.hidden,
              fieldMask: {
                paths: ['view_configuration__name', 'view_configuration__enabled'],
              },
            })
            .pipe(
              catchError(() => of(null)),
              map((resp) => resp?.ok || false),
            ),
        );
      } else {
        return firstValueFrom(
          this.viewConfigService
            .createViewConfigurationOverride({
              namespace: tab.namespace,
              identifier: tab.identifier,
              componentId: this.viewFilterKey(),
              name: tab.name,
              enabled: !tab.hidden,
              viewConfigurationId: tab.id,
              namespaceOverride: namespace,
              identifierOverride: userId,
            })
            .pipe(
              catchError(() => of(null)),
              map((resp) => !!resp),
            ),
        );
      }
    });
  }

  private saveCustomViewFilters(group: TabGroup<SavedFilter>, namespace: string, userId: string): Promise<boolean>[] {
    return group.tabs.map((tab) => {
      if (tab.id) {
        return firstValueFrom(
          this.viewConfigService
            .updateViewConfigurations({
              viewConfigurationId: tab.id,
              namespace: namespace,
              name: tab.name,
              filters: JSON.stringify(tab.filters),
              enabled: !tab.hidden,
              fieldMask: {
                paths: ['view__filter', 'view_configuration__name', 'view_configuration__enabled'],
              },
            })
            .pipe(
              catchError(() => of(null)),
              map((resp) => resp?.ok || false),
            ),
        );
      } else {
        return firstValueFrom(
          this.viewConfigService
            .createViewConfiguration({
              namespace: namespace,
              componentId: this.viewFilterKey(),
              name: tab.name,
              filters: JSON.stringify(tab.filters),
              enabled: !tab.hidden,
              identifier: userId,
            })
            .pipe(
              catchError(() => of(null)),
              map((resp) => !!resp),
            ),
        );
      }
    });
  }

  protected saveTabOrder($event: string[]) {
    try {
      localStorage.setItem(this.viewTabOrderKey(), JSON.stringify($event));
      this.tabOrder$$.next($event);
    } catch {
      this.snackService.openErrorSnack('VIEW_TABS.ERRORS.FAILED_TO_SAVE_VIEW_ORDER');
    }
    return;
  }

  protected toggleExcludeCampaignsOverride() {
    this.excludeCampaignsOverride.update((prev) => !prev);
  }
}
